import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { smoothEase } from '@/lib/motion'
import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/*                                Sparkle bits                                */
/* -------------------------------------------------------------------------- */

type SparkleProps = {
  className?: string
  size?: number
  delay?: number
}

/** Four-point twinkling star that pulses with a CSS animation. */
export function Sparkle({ className, size = 18, delay = 0 }: SparkleProps) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn(
        'inline-block animate-twinkle motion-reduce:animate-none',
        className
      )}
      style={{ animationDelay: `${delay}s` }}
    >
      <path
        fill="currentColor"
        d="M12 1.6c.4 4.7 1.7 6 6.4 6.4-4.7.4-6 1.7-6.4 6.4-.4-4.7-1.7-6-6.4-6.4 4.7-.4 6-1.7 6.4-6.4z"
      />
    </svg>
  )
}

type ScatterDot = {
  top: string
  left?: string
  right?: string
  size: number
  delay: number
  color: string
}

const DEFAULT_SCATTER: ScatterDot[] = [
  { top: '-12px', left: '-22px', size: 10, delay: 0, color: 'text-primary/70' },
  { top: '-4px', right: '-28px', size: 14, delay: 0.6, color: 'text-ink/40' },
  { top: '40px', left: '38%', size: 8, delay: 1.1, color: 'text-primary/55' },
  { top: '64px', right: '12%', size: 10, delay: 1.6, color: 'text-ink/30' },
  { top: '110px', left: '-10px', size: 9, delay: 2.1, color: 'text-primary/40' },
]

/** Constellation of small twinkling sparkles drifting around a headline. */
export function SparkleScatter({
  dots = DEFAULT_SCATTER,
  className,
}: {
  dots?: ScatterDot[]
  className?: string
}) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute -inset-x-6 -top-6 hidden h-40 sm:block',
        className
      )}
    >
      {dots.map((d, i) => (
        <span
          key={i}
          className={cn(
            'absolute animate-float-soft motion-reduce:animate-none',
            d.color
          )}
          style={{
            top: d.top,
            left: d.left,
            right: d.right,
            animationDelay: `${(d.delay * 0.6).toFixed(2)}s`,
          }}
        >
          <Sparkle size={d.size} delay={d.delay} />
        </span>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Decorated header                              */
/* -------------------------------------------------------------------------- */

type HeaderSize = 'hero' | 'section' | 'minor'
type HeaderAlign = 'left' | 'center'
type HeaderTag = 'h1' | 'h2' | 'h3'

type DecoratedHeaderProps = {
  /** Small all-caps text above the title (e.g. "The Portfolio") */
  eyebrow?: string
  /** Single word inside the eyebrow that gets the primary highlight */
  eyebrowAccent?: string
  /** Hide the trailing horizontal rule next to the eyebrow */
  hideEyebrowRule?: boolean
  /** Title text (string or ReactNode for fancy formatting) */
  title: ReactNode
  /** Optional supporting body paragraph */
  description?: ReactNode
  /** Heading element to render. Defaults based on size. */
  as?: HeaderTag
  /** Visual size: hero (page title), section (h2 block), minor (h3 column) */
  size?: HeaderSize
  /** Text alignment */
  align?: HeaderAlign
  /** Show floating sparkle constellation around the title (default: hero only) */
  scatter?: boolean
  /** Show the corner sparkle next to the title */
  sparkle?: boolean
  /** Show the animated underline behind the title */
  underline?: boolean
  /** Children rendered after description (e.g. CTA buttons or links) */
  children?: ReactNode
  /** Outer wrapper className */
  className?: string
  /** Override the eyebrow color tone for the accent word */
  eyebrowAccentClass?: string
  /** Disable the entrance animation (e.g. when caller is animating already) */
  noEnter?: boolean
}

const SIZE_TITLE: Record<HeaderSize, string> = {
  hero: 'font-display text-4xl font-semibold tracking-tight text-ink md:text-5xl',
  section:
    'font-display text-2xl font-semibold tracking-tight text-ink md:text-[1.75rem]',
  minor:
    'font-display text-xl font-semibold tracking-tight text-ink md:text-[1.4rem]',
}

const SIZE_UNDERLINE: Record<HeaderSize, string> = {
  hero: 'h-[6px] -bottom-1',
  section: 'h-[5px] -bottom-1',
  minor: 'h-[3px] -bottom-0.5',
}

const SIZE_SPARKLE: Record<HeaderSize, { wrap: string; size: number }> = {
  hero: { wrap: '-right-7 -top-2 h-5 w-5', size: 20 },
  section: { wrap: '-right-6 -top-1.5 h-4 w-4', size: 16 },
  minor: { wrap: '-right-5 -top-1 h-3.5 w-3.5', size: 14 },
}

const SIZE_DESCRIPTION: Record<HeaderSize, string> = {
  hero: 'mt-4 text-lg text-body',
  section: 'mt-3 max-w-2xl text-body',
  minor: 'mt-2 max-w-md text-sm leading-relaxed text-muted',
}

const SIZE_RULE: Record<HeaderSize, string> = {
  hero: 'ml-3 inline-block h-px w-10 align-middle bg-ink/30',
  section: 'ml-3 inline-block h-px w-8 align-middle bg-ink/25',
  minor: 'ml-2.5 inline-block h-px w-6 align-middle bg-ink/20',
}

/**
 * Decorated section/page header with subtle salon-style ornaments:
 * eyebrow with primary accent + horizontal rule, animated underline behind
 * the title, corner sparkle, and (optionally) a constellation of floating
 * sparkles around the headline. Designed to read as a coherent decorative
 * system across the site.
 */
export function DecoratedHeader({
  eyebrow,
  eyebrowAccent,
  hideEyebrowRule = false,
  title,
  description,
  as,
  size = 'section',
  align = 'left',
  scatter,
  sparkle = true,
  underline = true,
  children,
  className,
  eyebrowAccentClass,
  noEnter = false,
}: DecoratedHeaderProps) {
  const reduced = useReducedMotion()
  const Tag = (as ?? (size === 'hero' ? 'h1' : size === 'minor' ? 'h3' : 'h2')) as HeaderTag
  const showScatter = scatter ?? size === 'hero'
  const isCentered = align === 'center'

  const sparkleSpec = SIZE_SPARKLE[size]

  const motionProps = noEnter
    ? {}
    : {
        initial: reduced ? false : { opacity: 0, y: 12 },
        whileInView: reduced ? undefined : { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-48px' },
        transition: { duration: reduced ? 0 : 0.5, ease: smoothEase },
      }

  return (
    <motion.div
      className={cn(
        'relative',
        size === 'hero' && 'mb-10 max-w-2xl md:mb-14',
        size === 'section' && 'mb-8 md:mb-10',
        size === 'minor' && 'mb-5',
        isCentered && 'mx-auto text-center',
        size === 'hero' && isCentered && 'max-w-3xl',
        className
      )}
      {...motionProps}
    >
      {showScatter && !reduced && <SparkleScatter />}

      {eyebrow && (
        <p
          className={cn(
            'text-[10px] font-bold uppercase tracking-[0.32em] text-muted',
            isCentered && 'flex items-center justify-center gap-2'
          )}
        >
          {eyebrowAccent ? (
            <>
              <span className="inline-block align-middle">{eyebrow}</span>{' '}
              <span
                className={cn(
                  'inline-block align-middle',
                  eyebrowAccentClass ?? 'text-primary'
                )}
              >
                {eyebrowAccent}
              </span>
            </>
          ) : (
            <span className="inline-block align-middle">{eyebrow}</span>
          )}
          {!hideEyebrowRule && (
            <span aria-hidden className={SIZE_RULE[size]} />
          )}
        </p>
      )}

      <Tag className={cn(eyebrow ? 'mt-3' : '', SIZE_TITLE[size])}>
        <span className="relative inline-block pb-1">
          <span className="relative z-10">{title}</span>
          {underline && (
            <motion.span
              aria-hidden
              className={cn(
                'absolute left-0 w-full origin-left rounded-full bg-primary/15',
                SIZE_UNDERLINE[size]
              )}
              initial={reduced ? false : { scaleX: 0 }}
              whileInView={reduced ? undefined : { scaleX: 1 }}
              viewport={{ once: true, margin: '-48px' }}
              transition={{
                duration: reduced ? 0 : 0.9,
                delay: reduced ? 0 : 0.2,
                ease: smoothEase,
              }}
            />
          )}
          {sparkle && (
            <Sparkle
              className={cn('absolute text-primary', sparkleSpec.wrap)}
              size={sparkleSpec.size}
              delay={0.3}
            />
          )}
        </span>
      </Tag>

      {description && (
        <div
          className={cn(
            SIZE_DESCRIPTION[size],
            isCentered && 'mx-auto'
          )}
        >
          {description}
        </div>
      )}

      {children && <div className={cn(size === 'hero' ? 'mt-7' : 'mt-5')}>{children}</div>}
    </motion.div>
  )
}
